import React from 'react';
import { Card, CardContent, Typography, Divider, Box, Grid, Chip, FormControl, Select, MenuItem } from '@mui/material';

const TeamSummaryCard = ({ teamUid, teamEntry = {}, teamInfo = {}, categoriesMap = {}, divisionsMap = {}, eventsScores = {}, teamModes = {}, setTeamModes, setAutoAppliedModes, setManualOverrides, affiliatedMode }) => {
  if (!teamUid) return null;

  // collect affiliated events for this team
  const affiliated = Object.keys(eventsScores || {}).map(evUid => {
    const ev = eventsScores[evUid];
    if (!ev || !ev.event) return null;
    const assoc = (ev.event.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid));
    if (assoc.includes(teamUid)) {
      const num = ev.event.number;
      const name = ev.event.name;
      const company = ev.event.company;
      const label = num ? `#${num}${name ? ' - ' + name : ''}${company ? ' - ' + company : ''}` : (name || evUid);
      return { uid: evUid, label, avg: ev.avgOverall ?? '—' };
    }
    return null;
  }).filter(x => x);

  const showAdjusted = (affiliatedMode && affiliatedMode !== 'none') || Object.values(teamModes || {}).some(m => m !== 'normal');

  return (
    <Card sx={{ minWidth: 460 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6">{teamInfo.teamNumber ? `Team ${teamInfo.teamNumber}` : (teamEntry.teamNumber || '')} {teamInfo.teamName || teamEntry.teamName || ''}</Typography>
            <Typography variant="caption" color="text.secondary">{categoriesMap[teamInfo.category] || teamEntry.category || ''} • {divisionsMap[teamInfo.division] || teamEntry.division || ''}</Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2">Original Total</Typography>
            <Typography variant="h6">{teamEntry.originalTotal ?? '—'}</Typography>
            {showAdjusted && (
              <>
                <Typography variant="subtitle2">Adjusted Total</Typography>
                <Typography variant="h6">{teamEntry.adjustedTotal ?? '—'}</Typography>
              </>
            )}
            <Typography variant="subtitle2">Place</Typography>
            <Typography variant="h6">{teamEntry.place ?? teamEntry.overallPlace ?? '—'}</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Affiliated Events</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {affiliated.length === 0 && <Typography variant="body2">No affiliated events</Typography>}
              {affiliated.map(ev => (
                <Chip key={ev.uid} label={`${ev.label}`} size="small" />
              ))}
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <FormControl variant="standard" sx={{ minWidth: 160 }}>
                <Select
                  value={teamModes[teamUid] || 'normal'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (setManualOverrides) {
                      setManualOverrides(prev => ({ ...(prev || {}), [teamUid]: true }));
                    }
                    if (setAutoAppliedModes) {
                      setAutoAppliedModes(prev => {
                        const copy = { ...(prev || {}) };
                        delete copy[teamUid];
                        return copy;
                      });
                    }
                    if (setTeamModes) setTeamModes(prev => ({ ...prev, [teamUid]: v }));
                  }}
                  size="small"
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="total_average">Total Average</MenuItem>
                  <MenuItem value="affiliated_average" disabled={!(affiliated.length > 0)}>Affiliated Average (if below avg)</MenuItem>
                  <MenuItem value="affiliated_max" disabled={!(affiliated.length > 0)}>Affiliated Max (worst score)</MenuItem>
                  <MenuItem value="exclude" disabled={!(affiliated.length > 0)}>Affiliated Exclude (set to 1500)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TeamSummaryCard;
